import { Ref } from "react";
import { TFunction } from "i18next";
import { Select as MantineSelect, SelectProps as MantineSelectProps } from "@mantine/core";
import styles from "./styles.module.css";
import Loader from "../Loader";

export interface SelectProps extends MantineSelectProps {
  className?: string;
  loading?: boolean;
  variant?: "filled";
  ref?: Ref<HTMLInputElement>;
}

function Select({ loading, variant, ref, ...props }: SelectProps) {
  return (
    <MantineSelect
      {...props}
      ref={ref}
      radius={1}
      searchable
      // Prevents browser autofill from interfering with the select input (chrome fix by calling new-password)
      autoComplete="new-password"
      disabled={loading || props.disabled}
      classNames={{
        root: styles.root,
        input: `${styles.input} ${variant === "filled" ? styles.inputFilled : ""}`,
      }}
      {...(loading ? { rightSection: <Loader size="xs" />, rightSectionWidth: 28 } : {})}
    />
  );
}

export default Select;
/**
 * Transforms an array of objects into a format for a select component, with optional translation.
 *
 * @param data - The input array of objects or string.
 * @param valueKey - The key to use for the `value` property.
 * @param labelKey - The key to use for the `label` property.
 * @param t - The translation function from your i18n library (optional).
 * @param prefix - The prefix for the translation key (e.g., 'product.formats').
 * @param uppercase - Whether to convert the label to uppercase (optional).
 * @param lowercase - Whether to convert the label to lowercase (optional).
 * @param joiner - text elemt that joins multiple label parts (default is a space).
 * @returns An array of objects with `value` and `label` properties.
 */

export const transformDataToSelectOption = (
  data: any[],
  valueKey: string,
  labelKey: string | string[],
  t?: TFunction,
  prefix?: string,
  uppercase?: boolean,
  lowercase?: boolean,
  joiner = " "
) => {
  if (!data || !Array.isArray(data)) return [];

  const handleCasing = (label: string) => {
    if (uppercase) {
      return label.toUpperCase();
    } else if (lowercase) {
      return label.toLowerCase();
    } else {
      return label;
    }
  };

  return data.map((item) => {
    if (typeof item === "string") {
      const translationKey = prefix ? `${prefix}.${item}` : item;
      const translatedLabel = prefix && t ? t(translationKey) : item;
      return {
        value: item,
        label: handleCasing(translatedLabel),
      };
    }

    const keys = Array.isArray(labelKey) ? labelKey : [labelKey];
    const parts = keys
      .map((k) => getNestedValue(item, k))
      .filter((v) => v !== undefined && v !== null)
      .map((v) => String(v));

    const rawValue = getNestedValue(item, valueKey);
    const valueStr = rawValue == null ? "" : String(rawValue);

    if (parts.length === 0) {
      console.warn(`Label key(s) "${keys.join(",")}" not found in item:`, item);
      return { value: valueStr, label: valueStr };
    }

    const combinedLabel = parts.join(joiner);
    const translatedLabel = prefix && t && keys.length === 1 ? t(`${prefix}:${combinedLabel}`) : combinedLabel;

    return {
      value: valueStr,
      label: uppercase ? translatedLabel.toUpperCase() : translatedLabel,
    };
  });
};

const getNestedValue = (obj: any, path?: string) => {
  if (!obj || !path) return undefined;
  return path.split(".").reduce((acc: any, key: string) => {
    if (acc === undefined || acc === null) return undefined;
    return acc[key] !== undefined ? acc[key] : undefined;
  }, obj);
};
